import { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Settings, Lock, Building, Loader2, Camera, Trash2, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user, updateProfilePicture } = useAuth();
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      toast.error('Compila tutti i campi');
      return;
    }
    
    if (passwords.new !== passwords.confirm) {
      toast.error('Le nuove password non coincidono');
      return;
    }
    
    if (passwords.new.length < 6) {
      toast.error('La nuova password deve avere almeno 6 caratteri');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwords.current,
        new_password: passwords.new
      });
      toast.success('Password aggiornata con successo');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio password');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyUpdate = async (e) => {
    e.preventDefault();
    
    if (!companyName.trim()) {
      toast.error('Inserisci un nome valido');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/company?name=${encodeURIComponent(companyName)}`);
      toast.success('Nome azienda aggiornato');
      setCompanyName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine valido');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('L\'immagine deve essere inferiore a 2MB');
      return;
    }

    setUploadingImage(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result;
        
        try {
          const response = await axios.put(`${API}/auth/profile-picture`, {
            profile_picture: base64String
          });
          updateProfilePicture(response.data.profile_picture);
          toast.success('Immagine profilo aggiornata');
        } catch (error) {
          toast.error('Errore nel caricamento dell\'immagine');
        } finally {
          setUploadingImage(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Errore nel caricamento dell\'immagine');
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    setUploadingImage(true);
    try {
      await axios.delete(`${API}/auth/profile-picture`);
      updateProfilePicture(null);
      toast.success('Immagine profilo rimossa');
    } catch (error) {
      toast.error('Errore nella rimozione dell\'immagine');
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Impostazioni</h1>
        <p className="text-muted-foreground mt-1">Gestisci il tuo account e le preferenze</p>
      </div>

      {/* Profile Picture */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Camera size={20} className="text-primary" />
            Immagine Profilo
          </CardTitle>
          <CardDescription>Personalizza la tua immagine di profilo</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden border-2 border-primary/30">
                {user?.profile_picture ? (
                  <img 
                    src={user.profile_picture} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-primary font-heading font-bold text-3xl">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              {uploadingImage && (
                <div className="absolute inset-0 bg-background/80 rounded-full flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="h-11"
                  data-testid="upload-image-btn"
                >
                  <Camera size={16} className="mr-2" />
                  Carica immagine
                </Button>
                {user?.profile_picture && (
                  <Button
                    variant="outline"
                    onClick={handleRemoveImage}
                    disabled={uploadingImage}
                    className="text-destructive hover:text-destructive h-11"
                    data-testid="remove-image-btn"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Rimuovi
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                JPG, PNG o GIF. Massimo 2MB.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                data-testid="image-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <User size={20} className="text-primary" />
            Informazioni Account
          </CardTitle>
          <CardDescription>I tuoi dati di accesso</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-muted-foreground text-sm">Nome</Label>
              <p className="text-foreground font-medium">{user?.name}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-muted-foreground text-sm">Email</Label>
              <p className="text-foreground font-medium">{user?.email}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-muted-foreground text-sm">Ruolo</Label>
              <p className="text-foreground font-medium capitalize">{user?.role}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Lock size={20} className="text-primary" />
            Cambio Password
          </CardTitle>
          <CardDescription>Aggiorna la tua password di accesso</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current">Password attuale</Label>
              <Input
                id="current"
                type="password"
                value={passwords.current}
                onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                placeholder="••••••••"
                className="bg-background max-w-md"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new">Nuova password</Label>
              <Input
                id="new"
                type="password"
                value={passwords.new}
                onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                placeholder="••••••••"
                className="bg-background max-w-md"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Conferma nuova password</Label>
              <Input
                id="confirm"
                type="password"
                value={passwords.confirm}
                onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                placeholder="••••••••"
                className="bg-background max-w-md"
                data-testid="confirm-password-input"
              />
            </div>
            <Button 
              type="submit"
              disabled={loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="change-password-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Aggiorna Password
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Company Settings (Admin only) */}
      {user?.role === 'admin' && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Building size={20} className="text-primary" />
              Impostazioni Azienda
            </CardTitle>
            <CardDescription>Modifica i dati aziendali</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCompanyUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Nome Azienda</Label>
                <Input
                  id="companyName"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Inserisci il nuovo nome"
                  className="bg-background max-w-md"
                  data-testid="company-name-input"
                />
              </div>
              <Button 
                type="submit"
                disabled={loading}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="update-company-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Aggiorna Nome
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* App Info */}
      <Card className="bg-card border-border">
        <CardContent className="py-6">
          <div className="text-center text-muted-foreground text-sm">
            <p>IAnus - AI Budget Manager</p>
            <p className="text-xs mt-1">Versione 1.0.0</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
